<template>
    <kt-modal
        :visible="true"
        :title="recordTitle"
        size="modal-lg"
        @dismiss="onClose()"
    >
        <template #body>
            <mileage-form v-model="record" :pm-only="props.pmOnly"></mileage-form>
        </template>
    
        <template #footer>
            <div class="kt-modal-actions">
                <button @click="onClose()" class="btn btn-outline-secondary">
                    <X :size="16" class="me-1" />Close
                </button>
                <button v-if="record.Id" @click="deleteRecord()" class="btn btn-outline-danger">
                    <Trash2 :size="16" class="me-1" />Delete
                </button>
                <button @click="saveRecord()" class="btn btn-primary">
                    <Save :size="16" class="me-1" />Save
                </button>
            </div>
        </template>
        
    </kt-modal>
</template>

<script lang="ts" setup>
import { ref, watch } from "vue";
import { MileageModel } from "@/models/MileageModel";
import ApiService from "@/services/ApiService";
import Helper from "@/services/Helper";
import Alert from "@/services/Alert";
import KtModal from "@/components/UI/KtModal.vue";
import MileageForm from "@/components/Mileage/MileageForm.vue";
import { X, Trash2, Save } from 'lucide-vue-next';

const props = defineProps({
    recordId: {
        type: String,
        required: true
    },
    requestId: {
        type: [Number, String],
        default: null
    },
    clientId: {
        type: [Number, String],
        default: null
    },
    techId: {
        type: [Number, String],
        default: null
    },
    pmOnly: {
        type: Boolean,
        default: false
    }
});

const emit = defineEmits(['close']);

const recordTitle = ref("Mileage Entry");

const newRecord = () => {
    const mileage = new MileageModel();
    // Pre-populate with requestId if provided
    if (props.requestId) {
        mileage.RequestId = Number(props.requestId);
    }
    // Pre-populate with clientId if provided
    if (props.clientId) {
        mileage.CustomerNumber = Number(props.clientId);
    }
    // Pre-populate with techId if provided
    if (props.techId) {
        mileage.TechnicianId = Number(props.techId);
    }
    // Set default date to today
    const today = new Date();
    const year = today.getFullYear();
    const month = String(today.getMonth() + 1).padStart(2, '0');
    const day = String(today.getDate()).padStart(2, '0');
    mileage.DateAndTime = `${year}-${month}-${day}`;
    return mileage;
};

const record = ref<MileageModel>(newRecord());

const saveRecord = async () => {
    try {
        let data = Helper.prep(record.value);
        console.debug("save", data);
        data = await ApiService.saveMileage(data);
        // Close modal and refresh parent list
        emit('close');
    } catch (error) {
        console.error("Save failed:", error);
    }
};

const deleteRecord = async () => {
    Alert.notify("Deletion is currently disabled.");
};

const onClose = () => {
    emit('close');
};

const loadRecord = async (val: string) => {
    switch (val) {
        case "new":
            let tmp = newRecord();
            recordTitle.value = "New Mileage Entry";
            record.value = tmp;
            break;

        default:
            let rec = await ApiService.loadMileage(val);
            recordTitle.value = "Edit Mileage Entry";
            record.value = rec;
            break;
    }
};

// Load record when component mounts or recordId changes
watch(() => props.recordId, (newId) => {
    if (newId) {
        loadRecord(newId);
    }
}, { immediate: true });
</script>
